/**
 * BrandAnalyticsPanel — shared analytics render tree.
 *
 * Consumed by:
 *   • frontend/src/pages/owner/OverviewPage.jsx     (data via /api/owner/overview)
 *   • frontend/src/pages/admin/BrandDrilldownPage.jsx (data via /api/admin/brands/{id}/overview)
 *
 * Pure presentational component — no data fetching, no React Query hooks.
 * The parent passes the already-resolved overview response plus the
 * loading flags and the range / refresh handlers.
 *
 * EX-B Step 6 — all testIDs from Step 5 migrate verbatim:
 *   • kpi-{revenue|transactions|customers|stamps|redemptions|new-customers}-delta
 *   • settings-customer-growth-chart
 *   • settings-top-customers-table
 *   • settings-overview-last-updated
 *   • settings-overview-refresh-button
 *   • owner-kpis (kept name for backward-compat on the Owner page DOM)
 *   • owner-tx-chart, owner-top-branches, owner-top-rewards
 */
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from 'recharts';
import {
  Wallet, Receipt, Users, Stamp as StampIcon, Gift, UserPlus,
  RefreshCw, Trophy,
} from 'lucide-react';
import { formatRupiah } from '@/lib/utils';
import { SectionCard, RangeTabs, StatusPill } from '@/components/ui_primitives/Misc';
import KpiCard from '@/components/ui_primitives/KpiCard';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  // R10.6a (2026-07-05) — All Time preset. Backend `analytics.py`
  // floors the date filter to epoch for this bucket; receipt-summary
  // + branch-performance omit date_from/date_to when "all" is active.
  { value: 'all', label: 'All Time' },
];

const PREV_LABEL = {
  today: 'vs yesterday',
  '7d':  'vs prev 7d',
  '30d': 'vs prev 30d',
  '90d': 'vs prev 90d',
  all:   'historical',
};

const CHART_COLORS = ['#C9A84C', '#F0D080', '#937723', '#B8962A', '#473812'];

// PoP delta math (locked semantics — see Step 1 recon).
function popDelta(curr, prev, range) {
  const c = Number(curr) || 0;
  const p = Number(prev) || 0;
  const subtitle = PREV_LABEL[range] || `vs prev ${range}`;
  if (p === 0 && c > 0) return { direction: 'up', label: 'new', subtitle };
  if (p === 0 && c === 0) return { direction: 'flat', label: '0.0%', subtitle };
  const pct = ((c - p) / p) * 100;
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  const sign = pct > 0 ? '+' : '';
  return { direction, label: `${sign}${pct.toFixed(1)}%`, subtitle };
}

function formatClockHMS(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
  } catch (_) {
    return '—';
  }
}

function tooltipBody(label, value) {
  return (
    <div className="bg-[#0E0E13] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <div className="text-white/50">{label}</div>
      <div className="text-white font-semibold mt-0.5">{value}</div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  const txt = formatter ? formatter(v) : v;
  return tooltipBody(label, txt);
};

export default function BrandAnalyticsPanel({
  data, range, onRangeChange, onRefresh,
  isLoading = false, isFetching = false,
}) {
  const o = data || {};
  const m = o.metrics || {};
  const prev = o.previous_period || {};
  const spark = o.sparkline || { labels: [], revenue: [], transactions: [], stamps: [] };
  const txSeries = (spark.labels || []).map((lbl, i) => ({
    label: lbl?.slice(5) ?? '',
    transactions: spark.transactions?.[i] ?? 0,
    revenue: spark.revenue?.[i] ?? 0,
  }));
  const growthSeries = (spark.labels || []).map((lbl, i) => ({
    label: lbl?.slice(5) ?? '',
    new_customers: (o.customer_growth_series || [])[i] ?? 0,
  }));
  const topBranches = (o.top_branches || []).slice(0, 5);
  const topRewards = (o.top_rewards || []).slice(0, 5);
  const topCustomers = (o.top_customers || []).slice(0, 5);
  const growthIsAllZero = growthSeries.every((p) => p.new_customers === 0);
  const rangeLabel = RANGE_OPTIONS.find((r) => r.value === range)?.label;

  return (
    <div className="space-y-8" data-testid="brand-analytics-panel">
      {/* Range tabs + last-updated + refresh button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <RangeTabs value={range} onChange={onRangeChange} options={RANGE_OPTIONS} />
        <div
          className="flex items-center gap-2 text-xs text-white/55 font-mono"
          data-testid="settings-overview-last-updated"
        >
          <span>Updated {formatClockHMS(o.generated_at)}</span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching || isLoading}
            className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:bg-gold/10 hover:border-gold/30 text-white/70 hover:text-gold grid place-items-center transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="settings-overview-refresh-button"
            aria-label="Refresh analytics"
            title="Refresh"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="owner-kpis">
        <KpiCard
          label="Revenue"
          value={formatRupiah(m.revenue_rupiah)}
          sub={rangeLabel}
          spark={spark.revenue}
          icon={<Wallet size={14} />}
          delta={popDelta(m.revenue_rupiah, prev.revenue_rupiah, range)}
          testID="kpi-revenue"
        />
        <KpiCard
          label="Transactions"
          value={(m.transactions ?? 0).toLocaleString('id-ID')}
          sub="stamp scans"
          spark={spark.transactions}
          icon={<Receipt size={14} />}
          delta={popDelta(m.transactions, prev.transactions, range)}
          testID="kpi-transactions"
        />
        <KpiCard
          label="Active customers"
          value={(m.active_customers ?? 0).toLocaleString('id-ID')}
          sub="unique IDs in window"
          icon={<Users size={14} />}
          delta={popDelta(m.active_customers, prev.active_customers, range)}
          testID="kpi-customers"
        />
        <KpiCard
          label="Stamps issued"
          value={(m.stamps_issued ?? 0).toLocaleString('id-ID')}
          sub="across all branches"
          spark={spark.stamps}
          icon={<StampIcon size={14} />}
          delta={popDelta(m.stamps_issued, prev.stamps_issued, range)}
          testID="kpi-stamps"
        />
        <KpiCard
          label="Rewards redeemed"
          value={(m.rewards_redeemed ?? 0).toLocaleString('id-ID')}
          sub="window total"
          icon={<Gift size={14} />}
          delta={popDelta(m.rewards_redeemed, prev.rewards_redeemed, range)}
          testID="kpi-redemptions"
        />
        <KpiCard
          label="New customers"
          value={(m.new_customers ?? 0).toLocaleString('id-ID')}
          sub="first card joined"
          icon={<UserPlus size={14} />}
          delta={popDelta(m.new_customers, prev.new_customers, range)}
          testID="kpi-new-customers"
        />
      </div>

      {/* Transactions trend */}
      <SectionCard testID="owner-tx-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold">Transactions over time</h3>
            <p className="text-xs text-white/50 mt-1">Daily transaction volume across the window.</p>
          </div>
          <StatusPill variant="gold">{rangeLabel}</StatusPill>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={txSeries} margin={{ top: 8, left: -20, right: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#C9A84C" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(212,175,55,0.3)', strokeWidth: 1 }} />
              <Area type="monotone" dataKey="transactions" stroke="#C9A84C" strokeWidth={2} fill="url(#goldFill)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      {/* Customer growth */}
      <SectionCard testID="settings-customer-growth-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold">New customers</h3>
            <p className="text-xs text-white/50 mt-1">Daily count of first-time card joins across the window.</p>
          </div>
          <StatusPill variant="gold">{rangeLabel}</StatusPill>
        </div>
        {growthIsAllZero ? (
          <div className="text-sm text-white/40 py-12 text-center" data-testid="customer-growth-empty">
            No new customers in this range.
          </div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthSeries} margin={{ top: 8, left: -20, right: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldFillGrowth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C9A84C" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(212,175,55,0.3)', strokeWidth: 1 }} />
                <Area type="monotone" dataKey="new_customers" stroke="#C9A84C" strokeWidth={2} fill="url(#goldFillGrowth)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      {/* Top branches / Top rewards / Top customers */}
      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard testID="owner-top-branches">
          <h3 className="font-display text-lg font-semibold mb-1">Top branches</h3>
          <p className="text-xs text-white/50 mb-4">By revenue in this window.</p>
          {topBranches.length === 0 ? (
            <div className="text-sm text-white/40 py-12 text-center">No transactions yet in this window.</div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topBranches} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip formatter={(v) => formatRupiah(v)} />} cursor={{ fill: 'rgba(212,175,55,0.08)' }} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {topBranches.map((_b, i) => (
                      <Bar key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>

        <SectionCard testID="owner-top-rewards">
          <h3 className="font-display text-lg font-semibold mb-1">Top rewards</h3>
          <p className="text-xs text-white/50 mb-4">By redemption count in this window.</p>
          {topRewards.length === 0 ? (
            <div className="text-sm text-white/40 py-12 text-center">No redemptions yet in this window.</div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topRewards} layout="vertical" margin={{ left: 20, right: 24, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis dataKey="title" type="category" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(212,175,55,0.08)' }} />
                  <Bar dataKey="redemptions" radius={[0, 6, 6, 0]} fill="#C9A84C" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>

        <SectionCard testID="settings-top-customers-table">
          <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
            <Trophy size={16} className="text-gold" /> Top customers
          </h3>
          <p className="text-xs text-white/50 mb-4">By stamps earned in this window.</p>
          {topCustomers.length === 0 ? (
            <div className="text-sm text-white/40 py-12 text-center" data-testid="top-customers-empty">
              No customer activity in this range.
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-widest font-semibold text-white/40 border-b border-white/10">
                    <th className="text-left py-2 pl-1 pr-2 font-semibold">Customer</th>
                    <th className="text-right py-2 px-2 font-semibold">Stamps</th>
                    <th className="text-right py-2 pr-1 pl-2 font-semibold">Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((c, i) => (
                    <tr key={c.id || i} className="border-b border-white/5 last:border-b-0" data-testid={`top-customer-row-${i}`}>
                      <td className="py-3 pl-1 pr-2">
                        <div className="text-white text-sm font-medium truncate">{c.name}</div>
                        <div className="text-[11px] text-white/40 font-mono mt-0.5">{c.masked_membership_id || '—'}</div>
                      </td>
                      <td className="text-right py-3 px-2 text-gold font-semibold tabular-nums">
                        {(c.stamps_earned ?? 0).toLocaleString('id-ID')}
                      </td>
                      <td className="text-right py-3 pr-1 pl-2 text-white/70 tabular-nums">
                        {(c.transaction_count ?? 0).toLocaleString('id-ID')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
